import { DatePipe } from '@angular/common';
import { Component, computed, input, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { DeployStatusCode, PlanDeployStatus } from '@core/models/plan.model';
import { ICONS } from '@core/services/icons/icons.service';

import {
  StatusPillComponent,
  StatusPillVariant
} from '@shared/components/status-pill/status-pill.component';

import { DeployWarningsListComponent } from '../deploy-warnings-list/deploy-warnings-list.component';

/**
 * How a deploy is badged beside its date — and `SUCCESS` is deliberately absent.
 *
 * A dated deploy with nothing said about it went out; a badge is what the block uses to report
 * that something needs knowing. A green "OK" here would also make the claim the channel list's
 * counts exist to avoid: the deploy that mailed 73 of 111 routes succeeded as a *job*, and
 * "OK" beside its date reads as though all 111 were out.
 */
const STATUS_PILL: Partial<
  Record<DeployStatusCode, { variant: StatusPillVariant; key: string }>
> = {
  ERROR: { variant: 'danger', key: 'features.plan.deploy.status.error' },
  PROCESSING: { variant: 'warning', key: 'features.plan.deploy.status.processing' }
};

/**
 * What the latest deploy of one configuration did: when it ran and why it failed. The channel
 * list badges the outcome in a word; this is where the operator reads the rest, which the page
 * reported nowhere before.
 */
@Component({
  selector: 'app-deploy-status-detail',
  imports: [TranslatePipe, DatePipe, StatusPillComponent, DeployWarningsListComponent],
  template: `
    @if (reported()) {
      <div
        data-testid="deploy-status-detail"
        class="border-default flex flex-col gap-2 rounded-sm border px-3 py-2 text-xs">
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span class="text-text-tertiary">
            {{ 'features.plan.deploy.last_deploy_label' | translate }}
          </span>
          @if (settled(); as settled) {
            <span
              data-testid="deploy-status-at"
              class="text-text-secondary font-mono tabular-nums">
              {{ settled.updatedAt | date: 'short' }}
            </span>
          }
          @if (pill(); as pill) {
            <app-status-pill [variant]="pill.variant" [dot]="true">
              {{ pill.key | translate }}
            </app-status-pill>
          }
        </div>

        @if (settled(); as settled) {
          @if (failed().length > 0) {
            <div class="bg-danger-soft border-danger overflow-hidden rounded-sm border">
              <button
                type="button"
                data-testid="deploy-failed-uids-toggle"
                class="flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left transition-opacity hover:opacity-80"
                [attr.aria-expanded]="failedListOpen()"
                (click)="failedListOpen.set(!failedListOpen())">
                <i [class]="ICONS.warning" class="text-danger text-xs"></i>
                <span class="text-text-primary font-semibold">
                  {{
                    'features.plan.deploy.failed_list.heading'
                      | translate: { count: failed().length }
                  }}
                </span>
                <i
                  [class]="failedListOpen() ? ICONS.caretUp : ICONS.caretDown"
                  class="text-text-secondary ml-auto text-[10px]"></i>
              </button>

              @if (failedListOpen()) {
                <!-- Wrapped rather than one per line: a channel can fail forty of a hundred,
                     and a column of forty uids would push the date and the badge off screen. -->
                <div class="border-danger border-t px-3 py-2">
                  <p class="text-text-tertiary mb-1 text-[10.5px]">
                    {{ 'features.plan.deploy.failed_list.hint' | translate }}
                  </p>
                  <div
                    data-testid="deploy-failed-uids"
                    class="flex max-h-40 flex-wrap gap-x-3 gap-y-0.5 overflow-y-auto">
                    @for (uid of failed(); track uid) {
                      <span class="text-text-primary font-mono text-[11px] tabular-nums">
                        {{ uid }}
                      </span>
                    }
                  </div>
                </div>
              }
            </div>
          }

          @if (channelError(); as error) {
            <div class="flex flex-col gap-1">
              <span class="text-text-secondary font-medium">
                {{ 'features.plan.deploy.error_detail_label' | translate }}
              </span>
              <app-deploy-warnings-list
                [messages]="[error]"
                [dense]="true"
                [variant]="'danger'" />
            </div>
          }

          @if (settled.warnings.length > 0) {
            <div class="flex flex-col gap-1">
              <span class="text-text-secondary font-medium">
                {{ 'features.plan.deploy.warnings_label' | translate }}
              </span>
              <app-deploy-warnings-list [messages]="settled.warnings" [dense]="true" />
            </div>
          }

          @if (unexplained()) {
            <span data-testid="deploy-unknown-error" class="text-danger">
              {{ 'features.plan.deploy.unknown_error' | translate }}
            </span>
          }
        }
      </div>
    }
  `,
  styles: ``
})
export class DeployStatusDetailComponent {
  /** The configuration's latest deploy, or null when it has never deployed for this plan. */
  readonly status = input<PlanDeployStatus | null>(null);
  /** An accepted deploy whose own status has not been recorded here yet. */
  readonly sending = input(false);
  /**
   * The messages that were attempted and never got through, from the loaded listing — see
   * `failedWithoutSending`. Empty for a channel that sends one payload per configuration,
   * which has no rows to point at.
   *
   * **`null` means the failing rows are not known yet**, which is not the same claim as an
   * empty list and is why this is nullable: while a per-message channel's listing is in
   * flight, neither the channel's own error nor "unexplained" can be asserted, because the
   * list about to arrive is what decides both. Reading absent as none made GBE's joined
   * string — the reasons of the very rows it was about to list — flash on screen and then be
   * replaced.
   */
  readonly failedUids = input<string[] | null>([]);

  protected readonly ICONS = ICONS;

  /** Collapsed until asked for; the count in the header is what the block owes at a glance. */
  protected readonly failedListOpen = signal(false);

  /** Whether the listing has answered at all. See the `failedUids` input. */
  private readonly failedKnown = computed(() => this.failedUids() !== null);

  /** The failing rows to list; empty both when none failed and while that is still unknown. */
  protected readonly failed = computed(() => this.failedUids() ?? []);

  /**
   * Whether this configuration has anything to report. False renders nothing at all: a
   * configuration that has never deployed is absent from the plan's statuses, and an empty
   * bordered box on every such channel would claim otherwise.
   */
  protected readonly reported = computed(() => this.sending() || this.status() !== null);

  /** Absent for a plain success, which the date alone already reports. See `STATUS_PILL`. */
  protected readonly pill = computed(() => {
    if (this.sending()) return STATUS_PILL.PROCESSING;
    const status = this.status();
    return status === null ? undefined : STATUS_PILL[status.status];
  });

  /**
   * The deploy whose outcome is being reported, or null while one is in flight — the status
   * still held then describes the send this deploy is superseding, so its date, error and
   * warnings would all date the wrong attempt.
   */
  protected readonly settled = computed(() => (this.sending() ? null : this.status()));

  /**
   * The channel's own failure, withheld once individual messages are known to have failed:
   * GBE's string is then those rows' reasons joined, so it says nothing the rows do not and
   * names none of them. What it still answers for is a failure with no rows behind it — GAEL's
   * refused SFTP connection, a missing secret — which is readable nowhere else.
   */
  protected readonly channelError = computed(() => {
    const settled = this.settled();
    if (settled === null || !this.failedKnown() || this.failed().length > 0) return null;
    return settled.error;
  });

  /**
   * A failure the channel reported without saying anything about it. Named rather than left
   * blank, so the block does not read as though the detail had failed to load — but not while
   * the failing messages are listed, which already reports that something failed.
   */
  protected readonly unexplained = computed(() => {
    const settled = this.settled();
    return (
      settled?.status === 'ERROR' &&
      !settled.error &&
      settled.warnings.length === 0 &&
      this.failedKnown() &&
      this.failed().length === 0
    );
  });
}
